import { ref, computed } from 'vue';
import { formatBilledState as formatMetabaseBilledState, formatChargeType as formatMetabaseChargeType, formatCompletionState } from './metabaseFilterMapper';
import { TIME_LOCK_FILTER } from './constants';

/**
 * Composable for managing Fixed Fees table filters
 * Handles all filter states, validation, and query parameter generation
 */

// Default filter state (extracted to avoid duplication)
const getDefaultFilters = () => ({
  cm: {
    client: null,
    matter: null
  },
  // Entity Filters (IDs extracted from cm for API calls)
  clientId: null,
  matterId: null,
  // Status Filters
  completionState: 'all',
  billedState: 'WIP',
  // Time Lock status, independent of billedState (BR-019). Its own vocabulary
  // here; mapped to the API's nullable bool in queryParams.
  timeLockState: TIME_LOCK_FILTER.ALL,
  chargeType: null,
  // User/Practice Filters
  account: {},
  invoiceUserId: null,
  practiceAreaId: null,
  // Matters Involving Filter (like matters page API)
  scopeAccount: null,
  originating: false,
  involved: false,
  primary: false,
  reviewer: false,
  // Date Filters
  billableDateStart: null,
  billableDateEnd: null,
  // Search
  search: '',
  // Custom Fields (format: { fieldId: optionId })
  clientCustomFields: {},
  matterCustomFields: {},
  // Pagination
  page: 1,
  pageSize: 50
});
export function useFixedFeesFilters() {
  // Filter States
  const filters = ref(getDefaultFilters());

  // Filter Options
  const completionStateOptions = [{
    title: 'All',
    value: 'all'
  }, {
    title: 'Completed',
    value: 'true'
  }, {
    title: 'Not Completed',
    value: 'false'
  }];
  const chargeTypeOptions = [{
    title: 'All Types',
    value: null
  }, {
    title: 'Completion-based',
    value: 'CompletionBased'
  }, {
    title: 'Date-based',
    value: 'DateBased'
  }];

  // Computed - Active Filter Count
  const activeFilterCount = computed(() => {
    const f = filters.value;

    // Count active custom field filters (non-empty values)
    const clientCustomFieldCount = Object.values(f.clientCustomFields || {}).filter(v => v && v !== '').length;
    const matterCustomFieldCount = Object.values(f.matterCustomFields || {}).filter(v => v && v !== '').length;
    return [f.cm?.client?.id, f.cm?.matter?.id, f.completionState !== 'all', f.billedState && f.billedState !== 'all',
    // Count any billed state except 'all'
    f.timeLockState !== TIME_LOCK_FILTER.ALL, f.chargeType, f.invoiceUserId, f.practiceAreaId, f.scopeAccount,
    // Matters involving filter
    f.billableDateStart || f.billableDateEnd, f.search].filter(Boolean).length + clientCustomFieldCount + matterCustomFieldCount;
  });

  // Computed - Query Parameters for API
  const queryParams = computed(() => {
    const f = filters.value;
    const params = {
      page: f.page,
      pageSize: f.pageSize
    };

    // Helper to conditionally add parameter if value exists and not default
    const addIfExists = (key, value, defaultValue) => {
      if (value && value !== defaultValue) params[key] = value;
    };

    // Only include non-null/non-default filters
    addIfExists('clientId', f.clientId);
    addIfExists('matterId', f.matterId);
    addIfExists('completionState', f.completionState, 'all');
    // Always send billedState unless it's 'all' (WIP is the default and should be sent)
    if (f.billedState && f.billedState !== 'all') {
      params.billedState = f.billedState;
    }
    // ChargeTableFilter.TimeLocked is a nullable bool — omitted means all.
    if (f.timeLockState === TIME_LOCK_FILTER.LOCKED) {
      params.timeLocked = true;
    } else if (f.timeLockState === TIME_LOCK_FILTER.UNLOCKED) {
      params.timeLocked = false;
    }
    addIfExists('chargeType', f.chargeType);
    addIfExists('invoiceUserId', f.invoiceUserId);
    addIfExists('practiceAreaId', f.practiceAreaId);

    // Matters Involving Filter - match API format from matters page
    if (f.scopeAccount) {
      params.scopeAccount = f.scopeAccount.id || f.scopeAccount;
      if (f.primary) params.primary = true;
    }
    addIfExists('billableDateStart', f.billableDateStart);
    addIfExists('billableDateEnd', f.billableDateEnd);
    addIfExists('search', f.search);

    // Add client custom field filters (format: clientcustomfield[index].fieldid and clientcustomfield[index].optionid)
    const clientCustomFieldEntries = Object.entries(f.clientCustomFields || {});
    clientCustomFieldEntries.forEach(([fieldId, optionId], index) => {
      // Skip if empty string, null, or undefined (deselected)
      if (optionId && optionId !== '') {
        params[`clientcustomfield[${index}].fieldid`] = fieldId;
        // Only include optionid if not filtering for "Not assigned"
        if (optionId !== 'NOT_ASSIGNED') {
          params[`clientcustomfield[${index}].optionid`] = optionId;
        }
      }
    });

    // Add matter custom field filters (format: mattercustomfield[index].fieldid and mattercustomfield[index].optionid)
    const matterCustomFieldEntries = Object.entries(f.matterCustomFields || {});
    matterCustomFieldEntries.forEach(([fieldId, optionId], index) => {
      // Skip if empty string, null, or undefined (deselected)
      if (optionId && optionId !== '') {
        params[`mattercustomfield[${index}].fieldid`] = fieldId;
        // Only include optionid if not filtering for "Not assigned"
        if (optionId !== 'NOT_ASSIGNED') {
          params[`mattercustomfield[${index}].optionid`] = optionId;
        }
      }
    });
    return params;
  });

  // Computed - Metabase Filters (for dashboard embedding)
  // Parameter names MUST be PascalCase to match backend DashboardFilter model properties for ASP.NET model binding
  const metabaseFilters = computed(() => {
    const f = filters.value;
    const params = {};

    // Date range - backend expects PascalCase StartDate and EndDate in YYYY-MM-DD format for DateOnly parsing
    // Backend's ParameterValueFromFilter maps 'date' parameter to DateRangeFromFilter
    if (f.billableDateStart) {
      const startDate = f.billableDateStart instanceof Date ? f.billableDateStart : new Date(f.billableDateStart);
      params.StartDate = startDate.toNetworkDate();
    }
    if (f.billableDateEnd) {
      const endDate = f.billableDateEnd instanceof Date ? f.billableDateEnd : new Date(f.billableDateEnd);
      params.EndDate = endDate.toNetworkDate();
    }

    // Client and Matter IDs - backend expects Guid? types
    if (f.clientId) params.ClientId = f.clientId;
    if (f.matterId) params.MatterId = f.matterId;

    // Practice area and user IDs - backend expects Guid? types
    if (f.practiceAreaId) params.PracticeAreaId = f.practiceAreaId;
    if (f.invoiceUserId) {
      params.UserId = f.invoiceUserId;
    }

    // ResponsibleId - the responsible attorney of the matter (via matters involving filter)
    if (f.scopeAccount) {
      params.ResponsibleId = f.scopeAccount.id || f.scopeAccount;
    }

    // Billed state - backend expects bool? (not string)
    const billedValue = formatMetabaseBilledState(f.billedState);
    if (billedValue !== null) params.Billed = billedValue;

    // Fixed fee type - backend expects string (CompletionBased, DateBased, Recurring)
    const chargeType = formatMetabaseChargeType(f.chargeType);
    if (chargeType !== null) params.FixedFeeType = chargeType;

    // Completion status - backend expects string ('true' or 'false')
    const completionStatus = formatCompletionState(f.completionState);
    if (completionStatus !== null) params.CompletionStatus = completionStatus;
    return params;
  });

  // Methods
  const resetFilters = () => {
    filters.value = getDefaultFilters();
    // Override billedState to 'all' when clearing (not WIP)
    filters.value.billedState = 'all';
  };
  return {
    filters,
    completionStateOptions,
    chargeTypeOptions,
    activeFilterCount,
    queryParams,
    metabaseFilters,
    resetFilters
  };
}